// Das CRM: welche Seite bei welcher Adresse, im Rahmen mit der Seitenleiste
// (rahmen.tsx). Seit E3 je Rolle eine eigene Sicht (Gernots Entscheidung
// 3): Die Leiste zeigt nur, was die Rolle darf, „/“ ist ihre Startseite,
// und eine Seite, die nicht zu ihr gehört, zeigt einen freundlichen Hinweis
// statt eines Fehlers – der Server weist ohnehin ab (403). Welche Adresse
// welche Seite ist und was sie braucht, steht in navigation.ts.
//
// Gebaut in diesem Stand: die Übersicht (seit S4 Manuels Zahlen aus der
// Datenbank), „Zu prüfen“ und die Freigabe mit dem Material; seit S4 die
// Unterlagen samt der Unterlage für den Monteur (Druckansicht; die
// Materiallisten des Lagers gab es bis E3c);
// seit S5 und S7 Kunden & Anlagen, die Akte, das Standardmaterial und „Leute
// und Geräte“ (eine Seite, neu nach Grundsatz 1); seit S6 die Fragen – an
// jedem Stück der Besichtigung, in der Akte und als eigene Seite, dazu das
// Band „offene Fragen“ oben auf jeder Seite (fragen/, rahmen.tsx). Seit E3c
// die Seite „Lieferanten und Partien“ der Leitung (partner/); seit E3c, S2
// „Zu tun“ an Stelle von „Zu prüfen“ (zutun/) – die gesendeten Besichtigungen
// stehen weiter unter #/pruefen, in der Kiste Sales/Doku.

import { useEffect } from "react";
import type { IchAntwort } from "@happy-energy/kern";
import { Toast, useSeite, type AnmeldungHilfen } from "@happy-energy/oberflaeche";
import { AkteSeite } from "./akte/Akte.tsx";
import { FragenSeite } from "./fragen/FragenSeite.tsx";
import { FreigabeSeite, MaterialFenster } from "./freigabe/FreigabeSeite.tsx";
import { Kunden } from "./kunden/Kunden.tsx";
import { Leute } from "./Leute.tsx";
import { seiteErlaubt, seiteLesen, type Seite as SeiteArt } from "./navigation.ts";
import { PartnerSeite } from "./partner/PartnerSeite.tsx";
import { BestellungKiste, DachplanungKiste, TerminKiste } from "./kisten/KistenSeiten.tsx";
import { KistenSeite, NichtDeineRolleSeite, UnbekannteSeite } from "./Platzhalter.tsx";
import { Pruefen } from "./Pruefen.tsx";
import { Rahmen } from "./rahmen.tsx";
import { Standardmaterial } from "./standardliste/Standardmaterial.tsx";
import { Uebersicht } from "./Uebersicht.tsx";
import { UnterlageSeite } from "./unterlage/UnterlageSeite.tsx";
import { UnterlagenSeite } from "./unterlage/UnterlagenSeite.tsx";
import { CrmKontext } from "./zusammenhang.tsx";
import { ZuTunSeite } from "./zutun/ZuTunSeite.tsx";
import "./crm.css";

function SeiteZeigen({ seite, ich }: { seite: SeiteArt; ich: IchAntwort }) {
  switch (seite.art) {
    case "uebersicht":
      return <Uebersicht />;
    case "zu-tun":
      return <ZuTunSeite />;
    case "pruefen":
      return <Pruefen />;
    case "freigabe":
      return <FreigabeSeite key={seite.id} id={seite.id} />;
    case "material-fenster":
      // Ohne Rahmen – siehe unten.
      return null;
    case "fragen":
      return <FragenSeite />;
    case "kunden":
      return <Kunden />;
    case "anlage":
      return <AkteSeite key={seite.id} id={seite.id} {...(seite.stelle ? { stelle: seite.stelle } : {})} />;
    case "material":
      return <Standardmaterial />;
    case "partner":
      return <PartnerSeite />;
    case "unterlagen":
      return <UnterlagenSeite />;
    case "unterlage":
      return <UnterlageSeite key={seite.id} id={seite.id} />;
    case "kiste":
      // Seit E3c (S3, S4, S5): Bestellung, Termin und Dachplanung sind gebaut – die übrigen bleiben Platzhalter (Grundsatz 8).
      if (seite.schluessel === "bestellung") return <BestellungKiste />;
      if (seite.schluessel === "termin") return <TerminKiste />;
      if (seite.schluessel === "dachplanung") return <DachplanungKiste />;
      return <KistenSeite schluessel={seite.schluessel} />;
    case "leute":
    case "geraete":
      // Eine Seite – bei #/geraete gleich bei den Geräten.
      return <Leute ich={ich} bereich={seite.art} />;
    case "unbekannt":
      return <UnbekannteSeite />;
  }
}

export function Crm({ ich, hilfen }: { ich: IchAntwort; hilfen: AnmeldungHilfen }) {
  const pfad = useSeite();
  const seite = seiteLesen(pfad, ich.nutzer.rolle);
  const erlaubt = seiteErlaubt(ich.nutzer.rolle, seite);

  // Neue Seite: oben anfangen.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pfad]);

  return (
    <CrmKontext.Provider value={{ ich, hilfen }}>
      {seite.art === "material-fenster" && erlaubt ? (
        <MaterialFenster id={seite.id} />
      ) : (
        <Rahmen pfad={pfad}>
          {seite.art === "unbekannt" ? <UnbekannteSeite /> : erlaubt ? <SeiteZeigen seite={seite} ich={ich} /> : <NichtDeineRolleSeite />}
        </Rahmen>
      )}
      <Toast />
    </CrmKontext.Provider>
  );
}
